import isEqual from "lodash/isEqual";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import styled from "styled-components";
import assets from "../../common/assets";
import colors from "../../common/colors";
import { genericErrorConfig } from "../../common/exceptions";
import {
    canManageAdministrators,
    canManageOrderers,
    canSaveOrdererWithEmptyVisibleUnits,
} from "../../common/helpers/municipalityRoles";
import { unitsToListOptions } from "../../common/helpers/listOptions";
import { fontSize, space } from "../../common/variables";
import Button from "../../components/Button";
import Container from "../../components/Container";
import Cell, { CellHeader, CellHeading } from "../../components/Grid/Cell";
import CellBlock, {
    CellBlockFooter,
    CellBlockSection,
    CellBlockTitle,
    CellBlockValue,
} from "../../components/Grid/CellBlock";
import Grid from "../../components/Grid/Grid";
import Loader from "../../components/Loader";
import { ModalContext } from "../../components/Modal";
import OptionsList from "../../components/OptionsList";
import Plate from "../../components/Plate";
import SubHeader from "../../components/SubHeader";
import Tag from "../../components/Tag";
import TriggerFormInitially from "../../components/TriggerFormInitially";
import { AuthenticatedUserContext } from "../../contexts/AuthenticatedUserContext";
import { useSelectedUnits } from "../../hooks/useSelectedUnits";
import useSelectedMunicipalityId from "../../hooks/useSelectedMunicipalityId";
import useUnsavedChangesModal from "../../hooks/useUnsavedChangesModal";
import { useChangeOrdererRoleMutation } from "../../mutations/useChangeOrdererRoleMutation";
import { useDeleteOrdererMutation } from "../../mutations/useDeleteOrdererMutation";
import { useUpdateOrdererMutation } from "../../mutations/useUpdateOrdererMutation";
import { useAllUnitsQuery } from "../../queries/units/useAllUnitsQuery";
import { useOrdererByIdQuery } from "../../queries/useOrdererByIdQuery";
import { routes } from "../../routes";
import { applyHybrisUnitsAndFormErrors } from "../CustomerView/customerDetailsConfig";
import { ORDERER_ADMIN_UNITS_REQUIRED_MESSAGE } from "../AdministratorView/administratorDetailsConfig";
import { OrdererInfoFormData, useOrdererInfoForm } from "./OrdererDetailsView.hooks";
import OrdererInfoForm from "./OrdererInfoForm";
import OrdererInfoListing from "./OrdererInfoListing";
import {
    filterOrdererAuthorizedUnits,
    formatOrdererListingUnits,
    sortUnits,
} from "./OrdererView.utils";
import {
    ORDERER_ADMIN_OVERLAP_UNITS_HINT,
    ORDERER_UNITS_REQUIRED_MESSAGE,
    OrdererAccountWideErrors,
    convertOrdererForInput,
    convertOrdererForPromote,
    convertOrdererFromOutput,
    getChangeOrdererRoleErrorSubtitle,
    getModalOrdererErrorText,
    getOrdererAccountWideErrors,
    mapHybrisOrdererErrorField,
    withoutAccountWideErrors,
} from "./ordererDetailsConfig";
import { OrdererInput } from "./ordererTypes";

interface OrdererFormsEditing {
    information?: boolean;
    units?: boolean;
}

const isOrdererProfileChanged = (
    data: OrdererInfoFormData,
    original: ReturnType<typeof convertOrdererFromOutput>,
) =>
    data.firstName !== original.firstName ||
    data.lastName !== original.lastName ||
    data.phone !== original.phone ||
    data.email !== original.email ||
    data.customerCreationAllowed !== original.customerCreationAllowed;

const EditOrdererView = () => {
    const navigate = useNavigate();
    const municipalityId = useSelectedMunicipalityId();

    const { authUserData } = useContext(AuthenticatedUserContext);
    const { setModalOptions } = useContext(ModalContext);
    const { ordererId } = useParams<{ ordererId: string }>();

    const [isEditing, setIsEditing] = useState<OrdererFormsEditing | undefined>({
        information: false,
        units: false,
    });

    const [accountWideErrors, setAccountWideErrors] = useState<OrdererAccountWideErrors>({});
    const profileSavedBeforeRoleChange = useRef(false);
    /** Skip leave-edit on profile PUT success when a /role call will follow. */
    const keepEditingForRoleChange = useRef(false);
    /** Keep pending role radio across profile refetch during chained promote. */
    const pendingRoleDuringChange = useRef<OrdererInfoFormData["municipalityRole"] | null>(null);

    const ordererQuery = useOrdererByIdQuery(ordererId, {
        enabled: true,
    });

    const allUnitsQuery = useAllUnitsQuery();
    const allUnitsOptions = useMemo(
        () => unitsToListOptions(allUnitsQuery.data),
        [allUnitsQuery.data],
    );

    const ordererAuthorizedUnits = useMemo(
        () =>
            (ordererQuery.data &&
                allUnitsQuery.data &&
                filterOrdererAuthorizedUnits(ordererQuery.data, allUnitsQuery.data)) ||
            [],
        [allUnitsQuery.data, ordererQuery.data],
    );

    const { selectedUnits, setSelectedUnits, unitsError, setUnitsError, updateUnitList } =
        useSelectedUnits(ordererAuthorizedUnits);

    // Sync unit data into a state when API call returns with orderer's units
    useEffect(() => {
        if (ordererAuthorizedUnits) {
            setSelectedUnits(ordererAuthorizedUnits);
        }
    }, [ordererAuthorizedUnits, setSelectedUnits]);

    const resetSelectedUnits = () => {
        setSelectedUnits(ordererAuthorizedUnits);
    };

    const form = useOrdererInfoForm();
    const municipalityRole = form.watch("municipalityRole");
    const wantsOrdererAdmin = municipalityRole === "ORDERER_ADMIN";

    const storedRole = ordererQuery.data?.municipalityRole;
    const showRoleSelect =
        !!authUserData &&
        canManageAdministrators(authUserData.municipalityRole) &&
        storedRole !== "ORDERER_ADMIN";

    const deleteOrdererMutation = useDeleteOrdererMutation({
        onSuccess: () => {
            setModalOptions({ visible: false });
            navigate(-1);
        },
    });

    const updateOrdererMutation = useUpdateOrdererMutation({
        onSuccess: () => {
            if (keepEditingForRoleChange.current) {
                keepEditingForRoleChange.current = false;
                return;
            }

            setIsEditing(undefined);
        },
        onError: (error) => {
            keepEditingForRoleChange.current = false;
            pendingRoleDuringChange.current = null;

            setModalOptions(
                genericErrorConfig(
                    () => setModalOptions({ visible: false }),
                    "Kunde ej spara beställare.",
                    getModalOrdererErrorText(error),
                ),
            );

            const rejectedAccountWideFields = getOrdererAccountWideErrors(error);
            setAccountWideErrors(rejectedAccountWideFields);

            // Nothing was saved, and the current user can never save these
            // fields, so put them back to the values the orderer already has.
            Object.keys(rejectedAccountWideFields).forEach((fieldName) =>
                form.resetField(fieldName as keyof OrdererAccountWideErrors),
            );

            applyHybrisUnitsAndFormErrors(
                withoutAccountWideErrors(error),
                mapHybrisOrdererErrorField,
                (fieldName, fieldError) => form.setError(fieldName as any, fieldError),
                setUnitsError,
            );
        },
    });

    const changeOrdererRoleMutation = useChangeOrdererRoleMutation({
        onSuccess: () => {
            profileSavedBeforeRoleChange.current = false;
            pendingRoleDuringChange.current = null;
            setModalOptions({ visible: false });
            navigate(routes.administrators(municipalityId));
        },
        onError: (error) => {
            const afterProfileSave = profileSavedBeforeRoleChange.current;
            profileSavedBeforeRoleChange.current = false;
            pendingRoleDuringChange.current = null;
            const roleSubtitle = getChangeOrdererRoleErrorSubtitle(error);
            const subtitle = afterProfileSave
                ? ["Profiluppgifterna sparades, men rollen kunde inte ändras.", roleSubtitle]
                      .filter(Boolean)
                      .join(" ")
                : roleSubtitle;

            setModalOptions(
                genericErrorConfig(
                    () => setModalOptions({ visible: false }),
                    "Kunde ej ändra roll.",
                    subtitle,
                ),
            );

            applyHybrisUnitsAndFormErrors(
                error,
                mapHybrisOrdererErrorField,
                (fieldName, fieldError) => form.setError(fieldName as any, fieldError),
                setUnitsError,
            );
        },
    });

    const updateOrderer = (input: OrdererInput) => {
        updateOrdererMutation.mutate({
            ordererId: ordererId,
            input: input,
        });
    };

    const promoteOrderer = () => {
        changeOrdererRoleMutation.mutate({
            ordererId,
            input: convertOrdererForPromote(selectedUnits),
        });
    };

    const isOrdererAdmin = authUserData?.municipalityRole === "ORDERER_ADMIN";
    const allowEmptyVisibleUnits = canSaveOrdererWithEmptyVisibleUnits(
        authUserData?.municipalityRole,
    );

    const saveWithUnits = (data = convertOrdererFromOutput(ordererQuery.data)) => {
        if (!allowEmptyVisibleUnits && selectedUnits.length === 0) {
            setUnitsError(ORDERER_UNITS_REQUIRED_MESSAGE);
            return;
        }

        setAccountWideErrors({});
        updateOrderer(convertOrdererForInput(data, selectedUnits));
    };

    const showPromoteConfirmModal = (data: OrdererInfoFormData) => {
        setModalOptions({
            visible: true,
            title: "Gör till Beställare administratör?",
            subtitle: `Är du säker på att du vill ge ${ordererQuery.data?.firstName} ${ordererQuery.data?.lastName} rollen Beställare administratör? Personen flyttas till Administratörer.`,
            buttons: [
                <Button
                    size="small"
                    title="Bekräfta"
                    disabled={
                        changeOrdererRoleMutation.isPending || updateOrdererMutation.isPending
                    }
                    onClick={() => {
                        if (
                            changeOrdererRoleMutation.isPending ||
                            updateOrdererMutation.isPending
                        ) {
                            return;
                        }

                        setAccountWideErrors({});
                        pendingRoleDuringChange.current = data.municipalityRole;

                        const original = convertOrdererFromOutput(ordererQuery.data);
                        if (isOrdererProfileChanged(data, original)) {
                            keepEditingForRoleChange.current = true;
                            updateOrdererMutation.mutate(
                                {
                                    ordererId,
                                    input: convertOrdererForInput(data, selectedUnits),
                                },
                                {
                                    onSuccess: () => {
                                        profileSavedBeforeRoleChange.current = true;
                                        promoteOrderer();
                                    },
                                },
                            );
                            return;
                        }

                        profileSavedBeforeRoleChange.current = false;
                        promoteOrderer();
                    }}
                />,
                <Button
                    size="small"
                    title="Avbryt"
                    theme="inverted"
                    disabled={
                        changeOrdererRoleMutation.isPending || updateOrdererMutation.isPending
                    }
                    onClick={() => setModalOptions({ visible: false })}
                />,
            ],
        });
    };

    const showDeletionConfirmModal = () => {
        setModalOptions({
            visible: true,
            title: `Ta bort beställare?`,
            subtitle: `Är du säker på att du vill ta bort ${ordererQuery.data?.firstName} ${ordererQuery.data?.lastName}?`,
            buttons: [
                <Button
                    size="small"
                    title="Ta bort"
                    theme="red"
                    onClick={() => deleteOrdererMutation.mutate({ ordererId })}
                />,
                <Button
                    size="small"
                    title="Avbryt"
                    theme="inverted"
                    onClick={() => setModalOptions({ visible: false })}
                />,
            ],
        });
    };

    const submitInformation = (data: OrdererInfoFormData) => {
        if (showRoleSelect && data.municipalityRole === "ORDERER_ADMIN") {
            if (selectedUnits.length === 0) {
                setUnitsError(ORDERER_ADMIN_UNITS_REQUIRED_MESSAGE);
                return;
            }

            showPromoteConfirmModal(data);
            return;
        }

        saveWithUnits(data);
    };

    // Sync orderer data into a form when API call returns.
    // trigger() is required so listing isValid is computed; RHF leaves it
    // false until validation runs (TriggerFormInitially only mounts in edit).
    useEffect(() => {
        if (ordererQuery.isSuccess) {
            const converted = convertOrdererFromOutput(ordererQuery.data);
            const pendingRole = pendingRoleDuringChange.current;
            form.reset(
                pendingRole
                    ? { ...converted, municipalityRole: pendingRole }
                    : converted,
            );
            void form.trigger();
        }
    }, [form, ordererQuery.data, ordererQuery.isSuccess]);

    const resetForm = () => {
        form.reset(convertOrdererFromOutput(ordererQuery.data));
        void form.trigger();
    };

    const selectedUnitsHasChanged = !isEqual(
        sortUnits(selectedUnits),
        sortUnits(ordererAuthorizedUnits),
    );

    const overlapUnitsHint = isOrdererAdmin ? (
        <CellBlockSection>
            <OverlapUnitsHint>{ORDERER_ADMIN_OVERLAP_UNITS_HINT}</OverlapUnitsHint>
        </CellBlockSection>
    ) : null;

    const showUnsavedChangesModal = useUnsavedChangesModal();

    const toggleEditMode = (directToggleTo?: keyof OrdererFormsEditing) => {
        setAccountWideErrors({});

        if (!directToggleTo) {
            resetForm();
            resetSelectedUnits();
            setUnitsError(undefined);
            setIsEditing(undefined);
            return;
        }

        if (form.formState.isDirty || selectedUnitsHasChanged) {
            showUnsavedChangesModal(() => {
                resetForm();
                resetSelectedUnits();
                setIsEditing({
                    [directToggleTo]: true,
                });
            });
        } else {
            setIsEditing({
                [directToggleTo]: true,
            });
        }
    };

    const unitsRequiredForPromote = showRoleSelect && wantsOrdererAdmin;
    const unitsBlockSubmit =
        (unitsRequiredForPromote && selectedUnits.length === 0) ||
        (!unitsRequiredForPromote && !allowEmptyVisibleUnits && selectedUnits.length === 0);

    const isMutationLoading =
        deleteOrdererMutation.isPending ||
        updateOrdererMutation.isPending ||
        changeOrdererRoleMutation.isPending;

    useEffect(() => {
        const isRoleChangePending =
            changeOrdererRoleMutation.isPending || updateOrdererMutation.isPending;
        if (!isRoleChangePending) {
            return;
        }

        setModalOptions((current) => {
            if (!current.visible || current.title !== "Gör till Beställare administratör?") {
                return current;
            }

            return {
                ...current,
                buttons: [
                    <Button size="small" title="Bekräfta" disabled />,
                    <Button size="small" title="Avbryt" theme="inverted" disabled />,
                ],
            };
        });
    }, [
        changeOrdererRoleMutation.isPending,
        updateOrdererMutation.isPending,
        setModalOptions,
    ]);

    return (
        <Container>
            <SubHeader
                backArrow
                title={`${ordererQuery.data?.firstName} ${ordererQuery.data?.lastName}`}
            />
            <Plate padding="large" spaceBottom={true}>
                {ordererQuery.isLoading ? (
                    <Loader />
                ) : (
                    <Grid gutter={space.padding}>
                        <Cell gutter={space.padding}>
                            <CellHeading>Personuppgifter</CellHeading>
                            <CellBlock
                                active={isEditing?.information}
                                noOutlines={false}
                                onEdit={() => toggleEditMode("information")}>
                                {isEditing?.information ? (
                                    <>
                                        <CellBlockSection>
                                            <CellBlockTitle>
                                                Ändra personuppgifter
                                            </CellBlockTitle>
                                        </CellBlockSection>
                                        <CellBlockSection>
                                            <OrdererInfoForm
                                                onCancel={() => toggleEditMode(undefined)}
                                                {...form}
                                                accountWideErrors={accountWideErrors}
                                                showRoleSelect={showRoleSelect}
                                                isSubmitLoading={isMutationLoading}
                                                isNewForm={false}
                                                disableSubmit={unitsBlockSubmit}
                                                onSubmit={submitInformation}
                                            />
                                            <TriggerFormInitially trigger={form.trigger} />
                                        </CellBlockSection>
                                    </>
                                ) : (
                                    <OrdererInfoListing
                                        data={form.getValues()}
                                        isValid={form.formState.isValid}
                                        showRole={showRoleSelect}
                                    />
                                )}
                            </CellBlock>
                        </Cell>
                        <Cell gutter={space.padding}>
                            <CellHeader>
                                <CellHeading>
                                    Enheter
                                    {selectedUnits?.length > 0 && ` (${selectedUnits.length})`}
                                </CellHeading>
                                {isEditing?.units && selectedUnits.length > 0 && (
                                    <div>
                                        <Tag>
                                            {selectedUnits.length === 1
                                                ? `1 vald enhet`
                                                : `${selectedUnits.length} valda enheter`}
                                        </Tag>
                                    </div>
                                )}
                            </CellHeader>
                            {allUnitsOptions?.length || isEditing?.units ? (
                                <CellBlock
                                    onEdit={() => toggleEditMode("units")}
                                    active={isEditing?.units}>
                                    {!!allUnitsQuery.data?.length && isEditing?.units ? (
                                        <>
                                            <CellBlockSection>
                                                <CellBlockTitle>Ändra enheter</CellBlockTitle>
                                            </CellBlockSection>
                                            {overlapUnitsHint}
                                            <CellBlockSection>
                                                <OptionsList
                                                    overflowValue={space.medium}
                                                    setChoice={updateUnitList}
                                                    data={allUnitsOptions}
                                                    selectedData={selectedUnits}
                                                />
                                            </CellBlockSection>
                                            {(unitsError || unitsBlockSubmit) && (
                                                <CellBlockSection>
                                                    <CellBlockValue>
                                                        {unitsError ||
                                                            (unitsRequiredForPromote
                                                                ? ORDERER_ADMIN_UNITS_REQUIRED_MESSAGE
                                                                : ORDERER_UNITS_REQUIRED_MESSAGE)}
                                                    </CellBlockValue>
                                                </CellBlockSection>
                                            )}
                                            <CellBlockFooter
                                                isLoading={isMutationLoading}
                                                disableSubmit={
                                                    !selectedUnitsHasChanged ||
                                                    unitsBlockSubmit
                                                }
                                                hasChangesText={
                                                    selectedUnitsHasChanged
                                                        ? "Valet av enheter har osparade ändringar."
                                                        : undefined
                                                }
                                                onCancel={() => toggleEditMode(undefined)}
                                                onSubmit={() => saveWithUnits()}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <CellBlockSection>
                                                {selectedUnits.length > 0 ? (
                                                    selectedUnits.map((unit) => (
                                                        <CellBlockValue key={unit.id}>
                                                            {unit.name}
                                                        </CellBlockValue>
                                                    ))
                                                ) : (
                                                    <CellBlockValue>
                                                        {formatOrdererListingUnits(
                                                            selectedUnits,
                                                        )}
                                                    </CellBlockValue>
                                                )}
                                            </CellBlockSection>
                                            {overlapUnitsHint}
                                            {(unitsError ||
                                                (unitsRequiredForPromote &&
                                                    selectedUnits.length === 0)) && (
                                                <CellBlockSection>
                                                    <CellBlockValue>
                                                        {unitsError ||
                                                            ORDERER_ADMIN_UNITS_REQUIRED_MESSAGE}
                                                    </CellBlockValue>
                                                </CellBlockSection>
                                            )}
                                        </>
                                    )}
                                </CellBlock>
                            ) : (
                                <>
                                    <Button
                                        outsideTitle="Lägg till enheter"
                                        shape="round"
                                        onClick={() => toggleEditMode("units")}
                                        icon={assets.plus}
                                    />
                                    {overlapUnitsHint}
                                    {(unitsError || unitsBlockSubmit) && (
                                        <CellBlockValue>
                                            {unitsError ||
                                                (unitsRequiredForPromote
                                                    ? ORDERER_ADMIN_UNITS_REQUIRED_MESSAGE
                                                    : ORDERER_UNITS_REQUIRED_MESSAGE)}
                                        </CellBlockValue>
                                    )}
                                </>
                            )}
                        </Cell>
                    </Grid>
                )}
                {authUserData && canManageOrderers(authUserData.municipalityRole) && (
                    <Button
                        theme="inverted"
                        title="Ta bort"
                        disabled={isMutationLoading}
                        onClick={() => showDeletionConfirmModal()}
                        wrapperStyle={{
                            overflow: "hidden",
                            marginLeft: "auto",
                            marginTop: space.padding,
                        }}
                    />
                )}
            </Plate>
        </Container>
    );
};

const OverlapUnitsHint = styled(CellBlockValue)`
    display: block;
    font-size: ${fontSize.small};
    color: ${colors.grayDark};
`;

export default EditOrdererView;
